"use client";

import { c as _c } from "react/compiler-runtime";
import { ReactNode, createContext, useContext, useEffect, useState } from "react";
import { AuthSurface } from "@/components/auth";
import { MfaDialog } from "@/components/mfa";
import { Button } from "@/components/ui";
import { usePatientVerification } from "@/hooks/self-schedule/use-patient-verification";
import { type UseSelfScheduleReturn, useSelfScheduleRuntime } from "@/hooks/self-schedule/use-self-schedule";
const SelfScheduleContext = createContext<UseSelfScheduleReturn | undefined>(undefined);
const SelfScheduleUrlPrefillBannerContext = createContext<string | null>(null);
interface SelfScheduleProviderProps {
  children: ReactNode;
}
export function SelfScheduleProvider(t0) {
  const $ = _c(22);
  const {
    children
  } = t0;
  const {
    selfSchedule,
    urlPrefillMessage,
    waitlistLinkState,
    continueWithoutWaitlist
  } = useSelfScheduleRuntime();
  const {
    isResolvingSession,
    signOutPatient
  } = usePatientVerification();
  const [openSignIn, setOpenSignIn] = useState(false);
  let t1;
  let t2;
  if ($[0] !== isResolvingSession || $[1] !== waitlistLinkState) {
    t1 = () => {
      if (waitlistLinkState === "sign-in" && !isResolvingSession) {
        setOpenSignIn(true);
      }
    };
    t2 = [waitlistLinkState, isResolvingSession];
    $[0] = isResolvingSession;
    $[1] = waitlistLinkState;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  if (waitlistLinkState !== "none" && waitlistLinkState !== "ready") {
    let t3;
    if ($[4] !== isResolvingSession || $[5] !== openSignIn || $[6] !== signOutPatient || $[7] !== waitlistLinkState) {
      t3 = isResolvingSession || waitlistLinkState === "loading" ? <p role="status">Loading your waitlist booking preferences…</p> : waitlistLinkState === "sign-in" ? <><p>Sign in to book from your waitlist.</p><Button variant="secondary" onClick={() => setOpenSignIn(true)}>Sign in</Button><MfaDialog open={openSignIn} onOpenChange={setOpenSignIn} surface={AuthSurface.SELF_SCHEDULE} tAndSFooterText="booking from your waitlist" /></> : <><p role="alert">This waitlist link is no longer available or does not belong to your account.</p><Button variant="secondary" onClick={signOutPatient}>Sign in with another account</Button></>;
      $[4] = isResolvingSession;
      $[5] = openSignIn;
      $[6] = signOutPatient;
      $[7] = waitlistLinkState;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== continueWithoutWaitlist || $[10] !== isResolvingSession || $[11] !== waitlistLinkState) {
      t4 = !isResolvingSession && waitlistLinkState !== "loading" ? <Button variant="secondary" onClick={continueWithoutWaitlist}>Book an ordinary appointment instead</Button> : null;
      $[9] = continueWithoutWaitlist;
      $[10] = isResolvingSession;
      $[11] = waitlistLinkState;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    let t5;
    if ($[13] !== t3 || $[14] !== t4) {
      t5 = <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-foreground">{t3}{t4}</div>;
      $[13] = t3;
      $[14] = t4;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    return t5;
  }
  let t3;
  if ($[16] !== children || $[17] !== urlPrefillMessage) {
    t3 = <SelfScheduleUrlPrefillBannerContext.Provider value={urlPrefillMessage}>{children}</SelfScheduleUrlPrefillBannerContext.Provider>;
    $[16] = children;
    $[17] = urlPrefillMessage;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== selfSchedule || $[20] !== t3) {
    t4 = <SelfScheduleContext.Provider value={selfSchedule}>{t3}</SelfScheduleContext.Provider>;
    $[19] = selfSchedule;
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
}
export function useSelfScheduleUrlPrefillBanner() {
  return useContext(SelfScheduleUrlPrefillBannerContext);
}
export function useSelfScheduleContext() {
  const context = useContext(SelfScheduleContext);
  if (context === undefined) {
    throw new Error("useSelfScheduleContext must be used within a SelfScheduleProvider");
  }
  return context;
}

/**
 * Read `isRwgLanding` from the SelfScheduleProvider if one is mounted,
 * otherwise return false. Safe to call from components that are shared
 * with non-self-schedule surfaces (brand previews, settings pages,
 * billing receipts) where the provider isn't mounted.
 */
export function useIsRwgLanding() {
  const context = useContext(SelfScheduleContext);
  return context?.isRwgLanding ?? false;
}